import { addDays, addWeeks, startOfWeek, subWeeks } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import type {
  AppProfile,
  AuditLog,
  DayMeal,
  Lookup,
  Meal,
  MealTag,
  Selection,
  SystemSettings,
  WeekBundle,
} from '@/types'
import { APP_TIMEZONE } from '@/utils'

export const DEMO_STORAGE_KEY = 'viandas-demo-database-v3'
export const DEMO_SESSION_KEY = 'viandas-demo-session-v1'

export interface DemoDatabase {
  profiles: AppProfile[]
  weeks: WeekBundle[]
  meals: Meal[]
  selections: Selection[]
  sectors: Lookup[]
  shifts: Lookup[]
  locations: Lookup[]
  tags: MealTag[]
  settings: SystemSettings
  auditLogs: AuditLog[]
  credentials: Record<string, string>
}

const nowIso = () => new Date().toISOString()
const dateId = (value: Date) => formatInTimeZone(value, APP_TIMEZONE, 'yyyy-MM-dd')

const mealDefinitions: Array<[string, string, string, string, string[]]> = [
  ['meal-milanesa', 'Milanesa con puré', 'Milanesa dorada acompañada con un suave puré de papas.', '/meals/milanesa.svg', []],
  ['meal-pollo-papas', 'Pollo con papas', 'Pollo al horno con papas rústicas y hierbas frescas.', '/meals/pollo.svg', ['tag-low-sodium']],
  ['meal-pasta', 'Pasta con salsa', 'Pasta artesanal con salsa de tomates y albahaca.', '/meals/pasta.svg', ['tag-vegetarian']],
  ['meal-hamburguesa', 'Hamburguesa con papas', 'Hamburguesa casera con queso y papas al horno.', '/meals/burger.svg', []],
  ['meal-ensalada', 'Ensalada César', 'Hojas verdes, pollo grillado, croutones y aderezo César.', '/meals/salad.svg', []],
  ['meal-pastel', 'Pastel de papa', 'Carne especiada cubierta con puré gratinado.', '/meals/pastel.svg', []],
  ['meal-tarta', 'Tarta de verduras', 'Tarta integral con vegetales de estación y queso.', '/meals/tarta.svg', ['tag-vegetarian']],
  ['meal-pizza', 'Pizza casera', 'Masa de fermentación lenta, mozzarella y tomates.', '/meals/pizza.svg', ['tag-vegetarian']],
  ['meal-veggie', 'Bowl de vegetales', 'Arroz integral, vegetales asados, garbanzos y semillas.', '/meals/bowl.svg', ['tag-vegan', 'tag-gluten-free']],
]

function profile(
  id: string,
  firstName: string,
  lastName: string,
  username: string,
  email: string,
  sectorId: string | null,
  role: AppProfile['role'] = 'employee',
): AppProfile {
  return {
    id,
    firstName,
    lastName,
    username,
    email,
    role,
    employeeNumber: role === 'employee' ? `L-${id.slice(-3).toUpperCase()}` : null,
    sectorId,
    shiftId: role === 'employee' ? 'shift-morning' : null,
    locationId: 'location-central',
    active: true,
    lastSelectionAt: role === 'employee' ? nowIso() : null,
    restrictions: id === 'employee-sofia'
      ? [{ id: 'restriction-sofia', employeeId: id, type: 'allergy', notes: 'Alergia al maní.' }]
      : [],
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }
}

function createWeek(
  id: string,
  start: Date,
  status: WeekBundle['status'],
  meals: Meal[],
  deadline?: string,
): WeekBundle {
  const patterns = [
    ['meal-milanesa', 'meal-pollo-papas', 'meal-pasta'],
    ['meal-hamburguesa', 'meal-pollo-papas', 'meal-ensalada'],
    ['meal-pastel', 'meal-pasta', 'meal-milanesa'],
    ['meal-pollo-papas', 'meal-hamburguesa', 'meal-tarta'],
    ['meal-pizza', 'meal-milanesa', 'meal-ensalada', 'meal-veggie'],
  ]
  const days = patterns.map((mealIds, dayIndex) => {
    const date = dateId(addDays(start, dayIndex))
    const dayId = `${id}-day-${dayIndex + 1}`
    const dayMeals: DayMeal[] = mealIds.map((mealId, mealIndex) => {
      const meal = meals.find((item) => item.id === mealId)!
      return {
        id: `${dayId}-${mealId}`,
        weekDayId: dayId,
        mealId,
        displayOrder: mealIndex,
        active: true,
        meal,
        mealNameSnapshot: meal.name,
        mealDescriptionSnapshot: meal.description,
        mealImageUrlSnapshot: meal.imageUrl,
      }
    })
    return { id: dayId, weekId: id, date, serviceEnabled: true, note: null, dayMeals }
  })
  const end = addDays(start, 4)
  const priorSunday = addDays(start, -1)
  return {
    id,
    name: `Período ${formatInTimeZone(start, APP_TIMEZONE, 'd')} al ${formatInTimeZone(end, APP_TIMEZONE, 'd/MM')}`,
    startDate: dateId(start),
    endDate: dateId(end),
    selectionDeadline: deadline ?? fromZonedTime(`${dateId(priorSunday)}T18:00:00`, APP_TIMEZONE).toISOString(),
    status,
    createdBy: 'demo-admin',
    days,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }
}

function weekSelections(week: WeekBundle, profiles: AppProfile[], historical = false): Selection[] {
  const employees = profiles.filter((item) => item.role === 'employee')
  const result: Selection[] = []
  employees.forEach((employee, employeeIndex) => {
    const selectedDays = historical ? 5 : employeeIndex < 3 ? 5 : employeeIndex === 3 ? 2 : 0
    week.days.slice(0, selectedDays).forEach((day, dayIndex) => {
      const noMeal = (employeeIndex + dayIndex) % 8 === 6
      const meal = day.dayMeals[(employeeIndex + dayIndex) % day.dayMeals.length]?.meal
      result.push({
        id: `selection-${week.id}-${employee.id}-${day.id}`,
        employeeId: employee.id,
        weekId: week.id,
        weekDayId: day.id,
        mealId: noMeal ? null : meal?.id ?? null,
        noMeal,
        confirmed: historical || employeeIndex < 3,
        modifiedByAdmin: null,
        modifiedAt: null,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      })
    })
  })
  return result
}

export function createDemoDatabase(): DemoDatabase {
  const tags: MealTag[] = [
    { id: 'tag-vegetarian', name: 'Vegetariano', color: '#4f8f65' },
    { id: 'tag-vegan', name: 'Vegano', color: '#267557' },
    { id: 'tag-gluten-free', name: 'Sin TACC', color: '#b97a20' },
    { id: 'tag-low-sodium', name: 'Bajo en sodio', color: '#587da8' },
  ]
  const meals: Meal[] = mealDefinitions.map(([id, name, description, imageUrl, tagIds]) => ({
    id,
    name,
    description,
    imageUrl,
    active: true,
    tags: tags.filter((tag) => tagIds.includes(tag.id)),
    createdAt: nowIso(),
    updatedAt: nowIso(),
  }))
  const sectors: Lookup[] = [
    { id: 'sector-admin', name: 'Administración' },
    { id: 'sector-production', name: 'Producción' },
    { id: 'sector-maintenance', name: 'Mantenimiento' },
  ]
  const shifts: Lookup[] = [
    { id: 'shift-morning', name: 'Mañana' },
    { id: 'shift-afternoon', name: 'Tarde' },
    { id: 'shift-night', name: 'Noche' },
  ]
  const locations: Lookup[] = [{ id: 'location-central', name: 'Oficina Central' }]
  const profiles: AppProfile[] = [
    profile('demo-admin', 'Lucía', 'Fernández', 'admin', 'admin@empresa.com', 'sector-admin', 'admin'),
    profile('employee-juan', 'Juan', 'Pérez', 'juan.perez', 'juan.perez@empresa.com', 'sector-admin'),
    profile('employee-maria', 'María', 'López', 'maria.lopez', 'maria.lopez@empresa.com', 'sector-production'),
    profile('employee-carlos', 'Carlos', 'Gómez', 'carlos.gomez', 'carlos.gomez@empresa.com', 'sector-production'),
    profile('employee-sofia', 'Sofía', 'Martínez', 'sofia.martinez', 'sofia.martinez@empresa.com', 'sector-admin'),
    profile('employee-pedro', 'Pedro', 'Rodríguez', 'pedro.rodriguez', 'pedro.rodriguez@empresa.com', 'sector-maintenance'),
  ]
  const argentinaToday = fromZonedTime(`${formatInTimeZone(new Date(), APP_TIMEZONE, 'yyyy-MM-dd')}T12:00:00`, APP_TIMEZONE)
  const nextMonday = startOfWeek(addWeeks(argentinaToday, 1), { weekStartsOn: 1 })
  const activeWeek = createWeek('week-active', nextMonday, 'open', meals)
  const historicStart = subWeeks(nextMonday, 1)
  const historicWeek = createWeek('week-history', historicStart, 'finished', meals, subWeeks(new Date(), 1).toISOString())
  const selections = [
    ...weekSelections(activeWeek, profiles),
    ...weekSelections(historicWeek, profiles, true),
  ]

  return {
    profiles,
    weeks: [activeWeek, historicWeek],
    meals,
    selections,
    sectors,
    shifts,
    locations,
    tags,
    settings: {
      companyName: 'Andes & Compañía',
      systemName: 'Buen Plato',
      welcomeText: 'Elegí tus viandas del período de forma simple y rápida.',
      logoUrl: null,
      serviceWeekdays: [1, 2, 3, 4, 5],
      timezone: APP_TIMEZONE,
    },
    auditLogs: [],
    credentials: {
      'admin@empresa.com': 'demo123',
      admin: 'demo123',
      'juan.perez': '1234',
      'juan.perez@empresa.com': '1234',
      'maria.lopez': '1234',
      'maria.lopez@empresa.com': '1234',
      'carlos.gomez': '1234',
      'sofia.martinez': '1234',
      'pedro.rodriguez': '1234',
    },
  }
}

export function loadDemoDatabase(): DemoDatabase {
  try {
    const existing = localStorage.getItem(DEMO_STORAGE_KEY)
    if (existing) {
      const database = JSON.parse(existing) as DemoDatabase
      let snapshotsAdded = false
      database.weeks.forEach((week) => week.days.forEach((day) => day.dayMeals.forEach((dayMeal) => {
        if (!dayMeal.mealNameSnapshot?.trim()) {
          dayMeal.mealNameSnapshot = dayMeal.meal.name
          snapshotsAdded = true
        }
        if (dayMeal.mealDescriptionSnapshot === undefined) {
          dayMeal.mealDescriptionSnapshot = dayMeal.meal.description
          snapshotsAdded = true
        }
        if (dayMeal.mealImageUrlSnapshot === undefined) {
          dayMeal.mealImageUrlSnapshot = dayMeal.meal.imageUrl
          snapshotsAdded = true
        }
      })))
      if (snapshotsAdded) saveDemoDatabase(database)
      return database
    }
  } catch (error) {
    console.warn('No se pudo recuperar la base demo local.', error)
  }
  const database = createDemoDatabase()
  saveDemoDatabase(database)
  return database
}

export function saveDemoDatabase(database: DemoDatabase): void {
  localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(database))
}

export function resetDemoDatabase(): DemoDatabase {
  const database = createDemoDatabase()
  saveDemoDatabase(database)
  return database
}
